import { computed, nextTick, ref, watch } from "vue";

/**
 * The omnisearch's committed selection and the required-skill validity that
 * hangs off it. Owns the state; the root exposes the returned methods on the
 * host through `useExpose`, so the wizard's required-skill gate never has to
 * reach into the element's shadow DOM.
 *
 * @param {Object} options
 * @param {import('vue').Ref} options.inputEl - template ref to the activator field host exposing `inputElement`
 * @param {import('vue').Ref<boolean>} options.open - the dropdown's open state
 */
export function useOmnisearchSelection({ inputEl, open }) {
  // The last committed selection — required-skill validation (owned by the
  // wizard) reads `hasSelection`; editing the text clears it.
  const selected = ref(null);

  // Drive the quantum-select's selection highlight from the committed selection
  // so clearing the field (which sets `selected` to null) also clears the menu's
  // active row on reopen.
  const selectedValues = computed(() => (selected.value?.value != null ? [selected.value.value] : []));

  const customValidity = ref("");

  // Mirror the required-skill validity onto the inner input so typing /
  // clearing / selecting (which reset `customValidity`) drop the error state
  // immediately, not only at the next `reportValidity`.
  watch(customValidity, (message) => {
    inputEl.value?.inputElement?.setCustomValidity(message ?? "");
  });

  let suppressFocusOpen = false;

  // Consumed by the root's focusin handler: true exactly once after a
  // programmatic `focusInput`, so open-on-focus does not reopen a just-closed
  // menu.
  function consumeSuppressedFocusOpen() {
    if (suppressFocusOpen) {
      suppressFocusOpen = false;
      return true;
    }
    return false;
  }

  function hasSelection() {
    return selected.value != null;
  }

  function setCustomValidity(message) {
    customValidity.value = message ?? "";
  }

  function reportValidity() {
    // Force-invalidity for the required-skill gate is applied to the inner input
    // directly, so the shared sp-text-field keeps its message-only semantics (a
    // persistent custom-validity must not block other consumers).
    const input = inputEl.value?.inputElement;
    if (!input) {
      return true;
    }
    input.setCustomValidity(customValidity.value || "");
    input.reportValidity();
    return input.validity.valid;
  }

  function focusInput() {
    // Programmatic refocus must not trip open-on-focus into reopening the menu.
    // Clear on the next tick so an already-focused input (which fires no focusin
    // to consume the flag) can't leave it stuck and suppress a later genuine open.
    suppressFocusOpen = true;
    inputEl.value?.inputElement?.focus?.();
    nextTick(() => {
      suppressFocusOpen = false;
    });
  }

  function openSearch() {
    open.value = true;
    inputEl.value?.inputElement?.focus?.();
  }

  return {
    selected,
    selectedValues,
    customValidity,
    consumeSuppressedFocusOpen,
    hasSelection,
    setCustomValidity,
    reportValidity,
    focusInput,
    openSearch,
  };
}
